import { useState, useEffect, useRef } from "react";
import { projectURL } from "./utils/mockdata";
import CommentSound from "./audio/SubComment.mp3";

export const CreateSubComment = ({ commentId, onSubCommentCreated }) => {

    const [newSubComment, setNewSubComment] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionRange, setMentionRange] = useState({ start: -1, end: -1 });
    const [activeIndex, setActiveIndex] = useState(0);
    const subCommentAudioRef = useRef(null);
    const textareaRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        subCommentAudioRef.current = new Audio(CommentSound);
    }, []);

    const onSubCommentSubmit = (e) => {
        e.preventDefault();

        const userId = sessionStorage.getItem("loggedUserId");

        const dataInput = {
            commentId: commentId,
            subcomment: newSubComment,
            created_by: userId,
        };

        const subCommentPath = projectURL + "subcomments/create";

        try {
            fetch(subCommentPath, {
                headers: { "cors": "no-cors", "Content-Type": "application/json" },
                method: 'POST',
                body: JSON.stringify(dataInput),
            }).then(res => {
                //console.log("res status " + res.status);
                setNewSubComment('');
                onSubCommentCreated();
                subCommentAudioRef.current.play();
            });
        } catch (error) {
            //console.log("error " + error);
        }
    }

    // Handle input changes and detect @mentions
    const handleChange = (e) => {
        const val = e.target.value;
        const caret = e.target.selectionStart;
        setNewSubComment(val);

        const lastAt = val.lastIndexOf("@", caret - 1);
        if (lastAt !== -1 && (lastAt === 0 || /\s/.test(val[lastAt - 1]))) {
            const match = val.slice(lastAt + 1, caret);
            if (/^[\w]{0,20}$/.test(match)) {
                setMentionQuery(match);
                setMentionRange({ start: lastAt, end: caret });
                return;
            }
        }

        setMentionQuery("");
        setShowSuggestions(false);
        setSuggestions([]);
    };

    // Debounced fetch for suggestions
    useEffect(() => {
        if (!mentionQuery) return;
        const id = setTimeout(() => {
            fetch(`${projectURL}profiles/searchUsers?q=${encodeURIComponent(mentionQuery)}`)
                .then((res) => res.json())
                .then((data) => {
                    const users = data?.userList ?? [];
                    const names = users.map((u) => u.username).slice(0, 6);
                    setSuggestions(names);
                    setShowSuggestions(names.length > 0);
                    setActiveIndex(0);
                })
                .catch(() => {
                    setSuggestions([]);
                    setShowSuggestions(false);
                });
        }, 300);
        return () => clearTimeout(id);
    }, [mentionQuery]);

    // Click outside to close suggestions
    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const selectSuggestion = (username) => {
        const before = newSubComment.slice(0, mentionRange.start);
        const after = newSubComment.slice(mentionRange.end);
        const inserted = `@${username} `;
        const updated = before + inserted + after;
        setNewSubComment(updated);
        setMentionQuery("");
        setShowSuggestions(false);
        setTimeout(() => {
            textareaRef.current?.focus();
            const pos = (before + inserted).length;
            try { textareaRef.current.setSelectionRange(pos, pos); } catch (e) { }
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            if (mentionQuery) {
                e.preventDefault();
                selectSuggestion(suggestions[activeIndex]);
            }
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    return (
        <div className="">
            <form onSubmit={onSubCommentSubmit} className="w-6/6 max-w-4xl bg-white rounded-lg p-1 mx-auto">
                <span className="" ref={wrapperRef}>
                    <textarea
                        ref={textareaRef}
                        value={newSubComment}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Reply To"
                        className="block p-1.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        required
                    ></textarea>

                    {showSuggestions && suggestions.length > 0 && (
                        <ul className="absolute mt-2 bg-white border rounded shadow z-50 max-h-40 overflow-auto w-64 text-sm">
                            {suggestions.map((s, idx) => (
                                <li
                                    key={s}
                                    onClick={() => selectSuggestion(s)}
                                    className={`px-3 py-1 cursor-pointer hover:bg-gray-100 ${idx === activeIndex ? 'bg-gray-100' : ''}`}
                                >
                                    {s}
                                </li>
                            ))}
                        </ul>
                    )}
                </span>
                <span className="flex justify-end pt-1 px-1">
                    <button type="submit" className="text-gray-500 flex items-center hover:text-blue-800 hover:bg-gray-200 focus:outline-none focus:ring-blue-800 rounded-full text-[14px] px-1 py-1.5 text-center">
                        <svg
                            className="w-4 h-4 text-gray-800"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="1"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.5 8.046H11V6.119c0-.921-.9-1.446-1.524-.894l-5.108 4.49a1.2 1.2 0 0 0 0 1.739l5.108 4.49c.624.556 1.524.027 1.524-.893v-1.928h2a3.023 3.023 0 0 1 3 3.046V19a5.593 5.593 0 0 0-1.5-10.954Z"
                            />
                        </svg>
                        Reply
                    </button>
                </span>
            </form>
        </div>
    );
}
// export default CreateSubComment;
